<script>
import { onMount, onDestroy } from 'svelte';
import { modalState, hideModal } from '@app/store/modalStore.svelte.js';
import { projectState } from '@app/store/projectStore.svelte.js';
import { eventGroupState } from '@app/store/eventGroupStore.svelte.js';
import { styleState } from '@app/store/styleStore.svelte.js';
import { insertHistoryAction } from '@app/store/historyStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import { fade } from 'svelte/transition';
import * as m from '@app/paraglide/messages.js';
import decode from '@cclib/functions/decode.js';
import defaults from '@cclib/lib/defaults.js';
import alignCaptionFile from '@cclib/functions/special/alignCaptionFile.js';
import _Event from '@cclib/classes/event.js';
import _EventGroup from '@cclib/classes/eventGroup.js';
import convertToHtml from '@cclib/functions/quill/convertToHtml.js';
import tcLib from '@cclib/lib/timecode.js';
import { loadFfmpeg } from '@app/lib/loadFfmpeg.js';

const whisperLanguages = [
    { code: 'en', name: 'English' },
    { code: 'zh', name: 'Chinese' },
    { code: 'de', name: 'German' },
    { code: 'es', name: 'Spanish' },
    { code: 'ru', name: 'Russian' },
    { code: 'ko', name: 'Korean' },
    { code: 'fr', name: 'French' },
    { code: 'ja', name: 'Japanese' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'tr', name: 'Turkish' },
    { code: 'pl', name: 'Polish' },
    { code: 'ca', name: 'Catalan' },
    { code: 'nl', name: 'Dutch' },
    { code: 'ar', name: 'Arabic' },
    { code: 'sv', name: 'Swedish' },
    { code: 'it', name: 'Italian' },
    { code: 'id', name: 'Indonesian' },
    { code: 'hi', name: 'Hindi' },
    { code: 'fi', name: 'Finnish' },
    { code: 'vi', name: 'Vietnamese' },
    { code: 'he', name: 'Hebrew' },
    { code: 'uk', name: 'Ukrainian' },
    { code: 'el', name: 'Greek' },
    { code: 'ms', name: 'Malay' },
    { code: 'cs', name: 'Czech' },
    { code: 'ro', name: 'Romanian' },
    { code: 'da', name: 'Danish' },
    { code: 'hu', name: 'Hungarian' },
    { code: 'ta', name: 'Tamil' },
    { code: 'no', name: 'Norwegian' },
    { code: 'th', name: 'Thai' },
    { code: 'ur', name: 'Urdu' },
    { code: 'hr', name: 'Croatian' },
    { code: 'bg', name: 'Bulgarian' },
    { code: 'lt', name: 'Lithuanian' },
    { code: 'la', name: 'Latin' },
    { code: 'mi', name: 'Maori' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'cy', name: 'Welsh' },
    { code: 'sk', name: 'Slovak' },
    { code: 'te', name: 'Telugu' },
    { code: 'fa', name: 'Persian' },
    { code: 'lv', name: 'Latvian' },
    { code: 'bn', name: 'Bengali' },
    { code: 'sr', name: 'Serbian' },
    { code: 'az', name: 'Azerbaijani' },
    { code: 'sl', name: 'Slovenian' },
    { code: 'kn', name: 'Kannada' },
    { code: 'et', name: 'Estonian' },
    { code: 'mk', name: 'Macedonian' },
    { code: 'br', name: 'Breton' },
    { code: 'eu', name: 'Basque' },
    { code: 'is', name: 'Icelandic' },
    { code: 'hy', name: 'Armenian' },
    { code: 'ne', name: 'Nepali' },
    { code: 'mn', name: 'Mongolian' },
    { code: 'bs', name: 'Bosnian' },
    { code: 'kk', name: 'Kazakh' },
    { code: 'sq', name: 'Albanian' },
    { code: 'sw', name: 'Swahili' },
    { code: 'gl', name: 'Galician' },
    { code: 'mr', name: 'Marathi' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'si', name: 'Sinhala' },
    { code: 'km', name: 'Khmer' },
    { code: 'sn', name: 'Shona' },
    { code: 'yo', name: 'Yoruba' },
    { code: 'so', name: 'Somali' },
    { code: 'af', name: 'Afrikaans' },
    { code: 'oc', name: 'Occitan' },
    { code: 'ka', name: 'Georgian' },
    { code: 'be', name: 'Belarusian' },
    { code: 'tg', name: 'Tajik' },
    { code: 'sd', name: 'Sindhi' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'am', name: 'Amharic' },
    { code: 'yi', name: 'Yiddish' },
    { code: 'lo', name: 'Lao' },
    { code: 'uz', name: 'Uzbek' },
    { code: 'fo', name: 'Faroese' },
    { code: 'ht', name: 'Haitian Creole' },
    { code: 'ps', name: 'Pashto' },
    { code: 'tk', name: 'Turkmen' },
    { code: 'nn', name: 'Nynorsk' },
    { code: 'mt', name: 'Maltese' },
    { code: 'sa', name: 'Sanskrit' },
    { code: 'lb', name: 'Luxembourgish' },
    { code: 'my', name: 'Myanmar' },
    { code: 'bo', name: 'Tibetan' },
    { code: 'tl', name: 'Tagalog' },
    { code: 'mg', name: 'Malagasy' },
    { code: 'as', name: 'Assamese' },
    { code: 'tt', name: 'Tatar' },
    { code: 'haw', name: 'Hawaiian' },
    { code: 'ln', name: 'Lingala' },
    { code: 'ha', name: 'Hausa' },
    { code: 'ba', name: 'Bashkir' },
    { code: 'jw', name: 'Javanese' },
    { code: 'su', name: 'Sundanese' },
];

const modelSizes = [
    { value: 'tiny', label: 'Tiny (~75 MB)' },
    { value: 'base', label: 'Base (~142 MB)' },
    { value: 'small', label: 'Small (~466 MB)' },
    { value: 'medium', label: 'Medium (~1.5 GB)' },
    { value: 'large-v3-turbo', label: 'Large (~1.6 GB)' },
];

let language = $state('en');
let modelSize = $state('base');
let selectedEventGroupIndex = $state(-1);
let importMissingDialogue = $state(true);
let captionFileTimecodes = $state(false);
let syncSelectedOnly = $state(false);

let phase = $state('config');
let progress = $state(0);
let statusMsg = $state('');
let modelDownloaded = $state(false);
let duration = 10;
let durationTc = '00:00:10:00';

let advancedOpen = $state(false);
let downloadListener = null;

let eventGroupOptions = $derived(
    eventGroupState
        .map((group, index) => ({
            index,
            name: group.name || `Event Group ${index + 1}`,
            type: group.type,
            count: group.events?.length || 0,
        }))
        .filter((group) => group.type !== 'audio description' && group.count > 0)
);

let selectedGroup = $derived(eventGroupOptions.find((group) => group.index === Number(selectedEventGroupIndex)));
let canSyncSelectedOnly = $derived(!!selectedGroup && (eventGroupState[selectedGroup.index]?.selected?.length || 0) > 0);

onMount(async () => {
    initializeEventGroupSelection();
    await checkModelStatus();
});

onDestroy(() => {
    if (downloadListener) {
        window.ipcRenderer.removeListener('whisper-download-progress', downloadListener);
    }
});

function initializeEventGroupSelection() {
    if (!eventGroupOptions.length) {
        selectedEventGroupIndex = -1;
        return;
    }

    if (projectState.selected !== false) {
        const hasSelected = eventGroupOptions.find((group) => group.index === projectState.selected);
        if (hasSelected) {
            selectedEventGroupIndex = projectState.selected;
            return;
        }
    }

    selectedEventGroupIndex = eventGroupOptions[0].index;
}

function handleSelectedEventGroupChange() {
    if (!canSyncSelectedOnly) {
        syncSelectedOnly = false;
    }
}

async function checkModelStatus() {
    try {
        const result = await window.ipcRenderer.invoke('checkWhisperModel', { modelSize });
        modelDownloaded = result.exists;
    } catch {
        modelDownloaded = false;
    }
}

async function ensureModelDownloaded() {
    const modelCheck = await window.ipcRenderer.invoke('checkWhisperModel', { modelSize });
    if (modelCheck.exists) {
        modelDownloaded = true;
        return;
    }

    phase = 'downloading';
    progress = 0;
    statusMsg = m.offline_sync_status_downloading();

    downloadListener = (_event, data) => {
        progress = data.percent;
        statusMsg = `${m.offline_sync_status_downloading()} ${data.downloadedMB} / ${data.totalMB} MB`;
    };

    window.ipcRenderer.on('whisper-download-progress', downloadListener);

    try {
        await window.ipcRenderer.invoke('downloadWhisperModel', { modelSize });
    } finally {
        window.ipcRenderer.removeListener('whisper-download-progress', downloadListener);
        downloadListener = null;
    }

    modelDownloaded = true;
}

async function transcribeWithWhisper(mediaPath, modelPath, tempJsonPath) {
    const ffmpegModelPath = modelPath.replace(/^[A-Za-z]:/, '').replace(/\\/g, '/');
    const ffmpegJsonPath = tempJsonPath.replace(/^[A-Za-z]:/, '').replace(/\\/g, '/');

    phase = 'transcribing';
    progress = 0;
    statusMsg = m.offline_sync_status_starting();

    const ffmpeg = loadFfmpeg();

    return new Promise((resolve, reject) => {
        ffmpeg(mediaPath)
            .noVideo()
            .outputOptions([
                '-af', `whisper=model=${ffmpegModelPath}:language=${language}:destination=${ffmpegJsonPath}:format=json`,
                '-f', 'null',
            ])
            .output('-')
            .on('codecData', function (data) {
                durationTc = data.duration || '00:00:10:00';
                duration = tcLib.tcMsToSec(durationTc) || 10;
            })
            .on('progress', function (prog) {
                const tcSec = tcLib.tcMsToSec(prog.timemark);
                const percent = duration > 0 ? Number(((tcSec / duration) * 100).toFixed(2)) : 0;
                progress = Math.max(0, Math.min(100, percent));
                statusMsg = `${m.offline_sync_status_transcribing()} ${prog.timemark} / ${durationTc} (${progress}%)`;
            })
            .on('end', function () {
                resolve();
            })
            .on('error', function (err) {
                reject(err);
            })
            .run();
    });
}

function applyAlignedTimings(targetIndex, alignedEventGroup) {
    const targetGroup = eventGroupState[targetIndex];
    if (!targetGroup) {
        return;
    }

    if (syncSelectedOnly && targetGroup.selected.length > 0) {
        targetGroup.selected.forEach((selectedEventIndex) => {
            const alignedEvent = alignedEventGroup.events[selectedEventIndex];
            if (alignedEvent) {
                targetGroup.events[selectedEventIndex].start = alignedEvent.start;
                targetGroup.events[selectedEventIndex].end = alignedEvent.end;
            }
        });
        return;
    }

    targetGroup.events.forEach((event, eventIndex, events) => {
        const alignedEvent = alignedEventGroup.events[eventIndex];
        if (alignedEvent) {
            events[eventIndex].start = alignedEvent.start;
            events[eventIndex].end = alignedEvent.end;
        }
    });
}

function importMissingDialogueGroup(missingSegments) {
    if (!importMissingDialogue || missingSegments.length === 0) {
        return;
    }

    const eventGroupDefaults = JSON.parse(localStorage.getItem('cc-event-group-defaults')) || {};
    const newEventGroup = { ...(new _EventGroup({
        name: m.missing_dialogue_name({ index: eventGroupState.length }),
        type: 'subtitle',
        maxCps: eventGroupDefaults.maxCps || 9999,
        maxWpm: eventGroupDefaults.maxWpm || 9999,
        maxChars: eventGroupDefaults.maxChars || 9999,
        maxLines: eventGroupDefaults.maxLines || 9999,
        minDuration: eventGroupDefaults.minDuration || 0.5,
        maxDuration: eventGroupDefaults.maxDuration || 12,
        overlap: eventGroupDefaults.overlap,
        illegalChars: eventGroupDefaults.illegalChars,
    })) };

    missingSegments.forEach((segment) => {
        newEventGroup.events.push(
            { ...(new _Event({
                start: segment.start,
                end: segment.end,
                text: convertToHtml(segment.text),
            })) }
        );
    });

    eventGroupState.push(newEventGroup);
}

async function syncOffline() {
    if (!projectState.media.localPath) {
        toast.push(m.offline_sync_requires_local_media(), { classes: ['toast-warning'] });
        return;
    }

    const targetIndex = Number(selectedEventGroupIndex);
    if (!Number.isInteger(targetIndex) || !eventGroupState[targetIndex]) {
        toast.push(m.offline_sync_select_event_group(), { classes: ['toast-warning'] });
        return;
    }

    if (!eventGroupState[targetIndex].events?.length) {
        toast.push(m.offline_sync_no_events(), { classes: ['toast-warning'] });
        return;
    }

    const tempJsonPath = window.path.join(window.os.tmpdir(), `whisper-sync-${Date.now()}.json`);

    try {
        await ensureModelDownloaded();

        const modelInfo = await window.ipcRenderer.invoke('checkWhisperModel', { modelSize });
        await transcribeWithWhisper(projectState.media.localPath, modelInfo.path, tempJsonPath);

        phase = 'syncing';
        progress = 100;
        statusMsg = m.offline_sync_status_syncing();

        const jsonContent = await window.fs.readFile(tempJsonPath, 'utf8');
        const options = new defaults.options({
            profile: 'whisper',
            formatOptions: [
                {
                    name: 'Import Type',
                    type: 'list',
                    values: ['subtitle', 'transcription'],
                    selected: 'word map',
                },
                {
                    name: 'Max Characters',
                    type: 'number-input',
                    values: '',
                    selected: 32,
                },
                {
                    name: 'Max Lines',
                    type: 'number-input',
                    values: '',
                    selected: 3,
                },
            ],
            frameRate: projectState.frameRate,
            dropFrame: projectState.dropFrame,
            window: {
                width: styleState.width,
                height: styleState.height,
                xPadding: styleState.xPadding,
                yPadding: styleState.yPadding,
            },
        });

        const wordMap = await decode(jsonContent, options);
        const eventGroup = JSON.parse(JSON.stringify(eventGroupState[targetIndex]));

        if (eventGroup.events.length === 0) {
            toast.push(m.offline_sync_no_events(), { classes: ['toast-warning'] });
            phase = 'config';
            return;
        }

        eventGroup.events.forEach((event, i, events) => {
            events[i].start = i + 0.25;
            events[i].end = i + 0.75;
        });

        const alignmentRes = await alignCaptionFile(
            eventGroup,
            wordMap,
            projectState.frameRate,
            captionFileTimecodes
        );

        applyAlignedTimings(targetIndex, alignmentRes.eventGroup);
        importMissingDialogueGroup(alignmentRes.missingSegments || []);

        projectState.selected = targetIndex;

        insertHistoryAction({
            name: 'offline automatic sync',
            eventGroup: targetIndex,
            snapshots: [
                {
                    store: 'eventGroupState',
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });

        toast.push(m.timing_applied_success(), { classes: ['toast-success'] });
        hideModal();
    } catch (err) {
        console.error('Offline sync error:', err);
        toast.push(m.error_message({ error: err.message }), { classes: ['toast-warning'] });
        phase = 'config';
    } finally {
        try {
            await window.fs.unlink(tempJsonPath);
        } catch {}
    }
}
</script>

<div transition:fade={{ duration: 100 }} class="modal {modalState.selected === 'offlineSync' ? 'show d-block' : ''}" role="dialog" aria-labelledby="offlineSyncTitle" tabindex="-1" id="OfflineSyncModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="offlineSyncTitle">{m.offline_sync()}</h4>
                {#if phase === 'config'}
                    <button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
                {/if}
            </div>
            <div class="modal-body">
                {#if phase === 'config'}
                    <div class="mb-3">
                        <label for="offlineSyncLanguageSelect" class="form-label">{m.offline_sync_source_language()}</label>
                        <select id="offlineSyncLanguageSelect" class="form-select" bind:value={language} onchange={checkModelStatus}>
                            {#each whisperLanguages as lang (lang.code)}
                                <option value={lang.code}>{lang.name}</option>
                            {/each}
                        </select>
                    </div>

                    <div class="mb-3">
                        <label for="offlineSyncModelSelect" class="form-label">{m.offline_sync_model_size()}</label>
                        <select id="offlineSyncModelSelect" class="form-select" bind:value={modelSize} onchange={checkModelStatus}>
                            {#each modelSizes as model (model.value)}
                                <option value={model.value}>{model.label}</option>
                            {/each}
                        </select>
                        <div class="form-text">
                            {#if modelDownloaded}
                                <span class="text-success"><i class="bi bi-check-circle-fill"></i> {m.offline_sync_model_downloaded()}</span>
                            {:else}
                                <span class="text-muted"><i class="bi bi-cloud-download"></i> {m.offline_sync_model_download_on_first_use()}</span>
                            {/if}
                        </div>
                    </div>

                    <div class="mb-3">
                        <label for="offlineSyncEventGroupSelect" class="form-label">{m.offline_sync_event_group()}</label>
                        <select id="offlineSyncEventGroupSelect" class="form-select" bind:value={selectedEventGroupIndex} onchange={handleSelectedEventGroupChange}>
                            {#if !eventGroupOptions.length}
                                <option value={-1}>{m.offline_sync_no_event_groups()}</option>
                            {:else}
                                {#each eventGroupOptions as group (group.index)}
                                    <option value={group.index}>{group.name}</option>
                                {/each}
                            {/if}
                        </select>
                    </div>

                    <div class="accordion mb-3" id="offlineSyncAdvancedAccordion">
                        <div class="accordion-item">
                            <h2 class="accordion-header" id="offlineSyncAdvancedHeader">
                                <button class="accordion-button {advancedOpen ? '' : 'collapsed'}" type="button" onclick={() => (advancedOpen = !advancedOpen)} aria-expanded={advancedOpen} aria-controls="offlineSyncAdvancedCollapse">
                                    {m.offline_sync_advanced_options()}
                                </button>
                            </h2>
                            <div id="offlineSyncAdvancedCollapse" class="accordion-collapse collapse {advancedOpen ? 'show' : ''}" aria-labelledby="offlineSyncAdvancedHeader">
                                <div class="accordion-body">
                                    <div class="form-check mb-2">
                                        <input class="form-check-input" type="checkbox" id="offlineSyncImportMissingDialogue" bind:checked={importMissingDialogue}>
                                        <label class="form-check-label" for="offlineSyncImportMissingDialogue">
                                            {m.offline_sync_import_missing_dialogue()}
                                        </label>
                                    </div>
                                    <div class="form-check mb-2">
                                        <input class="form-check-input" type="checkbox" id="offlineSyncCaptionFileTimecodes" bind:checked={captionFileTimecodes}>
                                        <label class="form-check-label" for="offlineSyncCaptionFileTimecodes">
                                            {m.offline_sync_caption_file_timecodes()}
                                        </label>
                                    </div>
                                    {#if canSyncSelectedOnly}
                                        <div class="form-check">
                                            <input class="form-check-input" type="checkbox" id="offlineSyncSelectedOnly" bind:checked={syncSelectedOnly}>
                                            <label class="form-check-label" for="offlineSyncSelectedOnly">
                                                {m.offline_sync_selected_only()}
                                            </label>
                                        </div>
                                    {/if}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="d-grid">
                        <button class="btn btn-primary" onclick={syncOffline} disabled={!projectState.media.localPath || !eventGroupOptions.length}>
                            <i class="bi bi-clock-history"></i> {m.offline_sync_button()}
                        </button>
                    </div>

                    {#if !projectState.media.localPath}
                        <p class="text-center text-muted small mt-2">{m.offline_sync_requires_local_media()}</p>
                    {/if}
                    {#if !eventGroupOptions.length}
                        <p class="text-center text-muted small mt-2">{m.offline_sync_no_event_groups()}</p>
                    {/if}
                {:else}
                    <p>
                        {#if phase === 'downloading'}
                            <i class="bi bi-cloud-download" aria-hidden="true"></i> {m.offline_sync_status_downloading()}
                        {:else if phase === 'transcribing'}
                            <i class="bi bi-mic-fill" aria-hidden="true"></i> {m.offline_sync_status_transcribing()}
                        {:else if phase === 'syncing'}
                            <i class="bi bi-arrow-repeat" aria-hidden="true"></i> {m.offline_sync_status_syncing()}
                        {/if}
                    </p>
                    <div class="progress" role="progressbar" aria-label="Offline sync progress" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                        <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" style="width: {progress}%">{progress}%</div>
                    </div>
                    <p class="text-center text-muted small" aria-live="polite">{statusMsg}</p>
                {/if}
            </div>
        </div>
    </div>
</div>
